import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Label } from 'reactstrap';

import { blue } from '../../app/colors';
import { useThemeContext } from '../../app/theme-provider';
import { useToastr } from '../toastr';
import { Tooltip } from '../tooltip';

import parseAddress from './parse-address.mjs';

interface LocationLookupProps {
  id: string;
  onLookup: ({
    street,
    city,
    state,
    zip,
    country,
  }: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }) => void;
  className?: string;
}

export function LocationLookup({
  id,
  onLookup,
  className,
}: LocationLookupProps) {
  const { clearErrors } = useFormContext();
  const toast = useToastr();
  const autoCompleteComponent =
    useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const controlRef = useRef(false);
  const { theme } = useThemeContext();
  useEffect(() => {
    const handleVendorPlaceSelect = async (address: any) => {
      const {
        street,
        city,
        state,
        zip,
        country,
      }: {
        street: string;
        city: string;
        state: string;
        zip: string;
        country: string;
      } = parseAddress(address);
      onLookup({ street, city, state, zip, country });
      clearErrors();
      toast.success('Address fields have been populated');
    };
    if (!controlRef.current) {
      // eslint-disable-next-line unicorn/prefer-query-selector
      const inputElement = document.getElementById(id);
      if (!inputElement) return;
      autoCompleteComponent.current =
        new google.maps.places.PlaceAutocompleteElement({
          componentRestrictions: { country: 'us' },
        });
      autoCompleteComponent.current.id = 'place-autocomplete-input';
      // eslint-disable-next-line unicorn/prefer-dom-node-append
      inputElement?.appendChild(autoCompleteComponent.current);
      autoCompleteComponent.current.addEventListener(
        'gmp-select',
        async ({ placePrediction }: any) => {
          const place = placePrediction.toPlace();
          await place.fetchFields({
            fields: ['addressComponents'],
          });
          const { addressComponents } = place.toJSON();
          handleVendorPlaceSelect(addressComponents);
        }
      );
    }
    return () => {
      // Using a combination of both removeEventListener and using controlRef for cleanup
      autoCompleteComponent.current?.removeEventListener(
        'gmp-select',
        // eslint-disable-next-line unicorn/no-invalid-remove-event-listener
        async ({ placePrediction }: any) => {
          const place = placePrediction.toPlace();
          await place.fetchFields({
            fields: ['addressComponents'],
          });
          const { addressComponents } = place.toJSON();
          handleVendorPlaceSelect(addressComponents);
        }
      );
      controlRef.current = true;
    };
  }, [id, clearErrors, onLookup, toast]);

  return (
    <>
      <Label>Search For A Place</Label>
      <Tooltip tooltip="Use arrow keys once list appears to access the list after searching for address">
        <FontAwesomeIcon
          icon={faInfoCircle}
          size="lg"
          color={theme === 'dark' ? '#52baff' : blue}
          tabIndex={0}
          className="ms-1"
        />
      </Tooltip>
      <div id={id} className={className} />
    </>
  );
}
